import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PlusCircle, CheckCircle, XCircle } from "lucide-react";

// Configuración de la conexión al backend - DEBE SER LA MISMA QUE EN HomePage Y AdminPage
const API_BASE_URL = "https://supersofia.nvirred.com/consulta.php";

const CreatePointPage = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(null); // 'success', 'error', null
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setErrorMessage("");
    setLoading(true);

    if (!name.trim()) {
      setStatus("error");
      setErrorMessage("El nombre del punto de venta es obligatorio.");
      setLoading(false);
      return;
    }

    try {
      console.log("📤 Enviando datos al backend:", {
        name: name.trim(),
        description: description.trim(),
      });

      // Llamada real al backend
      const response = await fetch(`${API_BASE_URL}?route=payment-points`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        mode: "cors",
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
        }),
      });

      console.log("📡 Response status:", response.status);
      console.log("📡 Response headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Error response text:", errorText);
        throw new Error(`Error HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log("📦 Resultado del backend:", result);

      // Verificar si es un array (indica que se ejecutó GET en lugar de POST)
      if (Array.isArray(result)) {
        throw new Error("El backend devolvió una lista en lugar de crear un punto. Verifica la configuración del servidor.");
      }

      if (result.success) {
        setStatus("success");
        setName("");
        setDescription("");

        console.log("✅ Punto de venta creado exitosamente con ID:", result.id);

        // Redirigir al administrador después de un éxito
        setTimeout(() => {
          navigate("/admin");
        }, 1500);
      } else {
        const errorMsg = result.error || result.message || "Error desconocido del servidor";
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error("❌ Error completo al crear el punto de venta:", error);
      setStatus("error");
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8 pt-24 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div
        className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md border border-gray-200"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
          Crear Nuevo Punto de Venta
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="name"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Nombre del Punto de Venta
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-800"
              placeholder="Ej: Carnicería 'El Buen Bife'"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-gray-700 text-sm font-semibold mb-2"
            >
              Descripción (Opcional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="4"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-800 resize-y"
              placeholder="Una breve descripción de este punto de venta..."
              disabled={loading}
            ></textarea>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            className={`w-full font-semibold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 ${
              loading
                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
            }`}
            whileHover={loading ? {} : { scale: 1.03 }}
            whileTap={loading ? {} : { scale: 0.98 }}
          >
            <PlusCircle className="w-5 h-5" />
            {loading ? "Creando..." : "Crear Punto de Venta"}
          </motion.button>

          {status === "success" && (
            <motion.div
              className="mt-4 p-3 bg-green-100 text-green-700 rounded-xl flex items-center gap-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CheckCircle className="w-5 h-5" />
              Punto de venta creado con éxito. Redirigiendo...
            </motion.div>
          )}
          {status === "error" && (
            <motion.div
              className="mt-4 p-3 bg-red-100 text-red-700 rounded-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start gap-2">
                <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Error al crear el punto de venta</p>
                  {errorMessage && (
                    <p className="text-sm mt-1 opacity-90">{errorMessage}</p>
                  )}
                  <p className="text-xs mt-2 opacity-75">
                    Revisa la consola del navegador para más detalles técnicos.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </form>

        {/* Información de debugging para desarrollo */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">
              Información técnica (para debugging)
            </summary>
            <div className="mt-2 space-y-1">
              <p><strong>API URL:</strong> {API_BASE_URL}</p>
              <p><strong>Estado:</strong> {loading ? "Cargando..." : "Listo"}</p>
              <p><strong>Último estado:</strong> {status || "Ninguno"}</p>
            </div>
          </details>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CreatePointPage;